import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { authApi } from '@/lib/api';
import { authStore } from '@/lib/auth/authStore';
import { useAuth } from '@/hooks/useAuth';
import { roleSatisfies } from '@/hooks/usePermissions';
import { useSettings } from '@/hooks/useSettings';
import { Button, PageSpinner } from '@/components/ui';
import { FeatureDisabledPage, ForbiddenPage } from '@/features/errors/ErrorPages';
import type { Audience, FeatureFlag } from './registry';

/**
 * Wraps every authenticated route.
 *  - confirms the stored token with /auth/me on first load (spinner meanwhile)
 *  - anonymous -> /login (remembers where you were going)
 *  - must_change_password -> /change-password (the server blocks everything else anyway)
 */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { status, user, mustChangePassword } = useAuth();
  const location = useLocation();
  const [bootError, setBootError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (status !== 'unknown') return;
    let cancelled = false;
    authApi
      .me()
      .then((u) => {
        if (!cancelled) authStore.setUser(u);
      })
      .catch((e: { status?: number; message?: string }) => {
        // 401 is handled globally (signs out). Anything else: let the user retry.
        if (!cancelled && e.status !== 401) setBootError(e.message ?? 'Cannot reach the server.');
      });
    return () => {
      cancelled = true;
    };
  }, [status, attempt]);

  if (status === 'unknown') {
    if (bootError) {
      return (
        <div
          role="alert"
          className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center"
        >
          <p className="font-medium text-danger">{bootError}</p>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                setBootError(null);
                setAttempt((n) => n + 1);
              }}
            >
              Retry
            </Button>
            <Button variant="outline" onClick={() => authStore.clear()}>
              Sign out
            </Button>
          </div>
        </div>
      );
    }
    return <PageSpinner label="Signing in" />;
  }
  if (status === 'anonymous' || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return <>{children ?? <Outlet />}</>;
}

/**
 * Role + feature-flag guard for a route (or a section of UI).
 *
 *   <RequireRole audience="admin">...</RequireRole>
 *   <RequireRole audience="all" feature="rooms_enabled" />   // as a layout route (renders <Outlet/>)
 *
 * Unauthorised users see the 403 page in place (URL unchanged); a disabled feature shows the "feature off" page.
 * UI hiding is a convenience: the API enforces the same rules.
 */
export function RequireRole({
  audience,
  feature,
  children,
}: {
  audience: Audience;
  feature?: FeatureFlag;
  children?: ReactNode;
}) {
  const { user } = useAuth();
  const { settings, isReady } = useSettings();

  if (!roleSatisfies(user?.role, audience)) return <ForbiddenPage />;
  if (feature) {
    if (!isReady) return <PageSpinner />;
    if (!settings[feature]) return <FeatureDisabledPage feature={feature} />;
  }
  return <>{children ?? <Outlet />}</>;
}
